import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { SHEETS, forceRefreshSheet, API_BASE } from "../utils/api";
import { getMetaBulkStatus, sendBulkViaMeta } from "../utils/whatsapp";
import { usePolling } from "../utils/usePolling";
import { useCreativesFilters } from "../contexts/CreativesFiltersContext";

const API_URL = SHEETS.promotional;
const CACHE_KEY = "promotional_cache_v1";
// v2 — switched dropdown source to "Short Name" column. Bumping the key
// forces a fresh fetch on existing users so the old full-name cache is
// discarded and the short-name list shows up immediately.
const FIRMS_CACHE_KEY = "promotional_firms_cache_v2";

// Analytics webhook — POSTs one row per download with the dealer's
// details so we can count who downloaded what. Each row lands in the
// "Sota Creative Downloads" sheet's Downloads tab; per-dealer and
// per-creative summaries auto-compute on the linked tabs.
const DOWNLOAD_ANALYTICS_URL =
  "https://script.google.com/macros/s/AKfycbxOhdO_EBdLfH_JMY46o_PLZ8KI1Y95aF5OktgnrMXWuzVOyJAqX_5fRAundgsi4XAEGw/exec";

// Fire-and-forget POST of a single download event. mode: "no-cors"
// because Google Apps Script web apps don't return CORS headers — the
// browser still sends the request and the script runs, we just can't
// read the response (which we don't need to).
async function logCreativeDownload(payload) {
  if (!DOWNLOAD_ANALYTICS_URL) return;
  try {
    await fetch(DOWNLOAD_ANALYTICS_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify(payload),
    });
  } catch {
    /* analytics is best-effort */
  }
}

/* ---------- helpers ---------- */
// Classify a row as "Festival" or "Promotional" using the Type / Category
// column from the sheet. Falls back to "Promotional" when missing so legacy
// rows still appear under the Promotional tab.
const detectType = (row) => {
  const t = String(
    row?.Type ||
      row?.type ||
      row?.TYPE ||
      row?.Category ||
      row?.category ||
      row?.CATEGORY ||
      ""
  )
    .trim()
    .toLowerCase();
  if (t.includes("festiv") || t.includes("greeting") || t === "personal") {
    return "Festival";
  }
  if (t.includes("promo")) return "Promotional";
  return "Promotional";
};

// Convert any date-ish value to "YYYY-MM-DD" for filter comparison.
const toInputDate = (val) => {
  if (!val) return "";
  const s = String(val).trim();
  if (!s) return "";
  // DD/MM/YYYY (Indian) — must check first because new Date() parses as MM/DD
  const m = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2,4})$/);
  if (m) {
    let day = Number(m[1]);
    let month = Number(m[2]) - 1;
    let year = Number(m[3]);
    if (year < 100) year += 2000;
    const dt = new Date(year, month, day);
    if (!isNaN(dt.getTime())) {
      const y = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      return `${y}-${mm}-${dd}`;
    }
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
};

const toDriveDirect = (url) => {
  if (!url) return "";
  const m = String(url).match(/(?:\/d\/|id=)([a-zA-Z0-9_-]{20,})/);
  if (m) return `https://drive.google.com/thumbnail?id=${m[1]}&sz=w2000`;
  return url;
};

// Extract Drive file ID from any Drive URL form (file/d/ID, ?id=ID, etc).
// Used to build lh3.googleusercontent.com URLs which behave more reliably
// for download-time canvas use than drive.google.com/thumbnail.
const driveIdFromUrl = (url) => {
  if (!url) return null;
  const m = String(url).match(/(?:\/d\/|id=)([a-zA-Z0-9_-]{20,})/);
  return m ? m[1] : null;
};

// Build a Drive "force-download" URL from any Drive URL. Hitting this URL
// makes Drive respond with `Content-Disposition: attachment`, which causes
// the browser to save the file instead of displaying it inline. The
// browser uses the user's existing Drive session for the request, so the
// REAL file bytes come back (not the anonymous server-fetch placeholder
// we hit when going through our backend proxy).
const driveDownloadUrl = (url) => {
  const id = driveIdFromUrl(url);
  if (!id) return null;
  return `https://drive.google.com/uc?export=download&id=${id}`;
};

// Convert ISO date or any date-ish string to "DD/MM/YYYY". Returns "" if invalid.
const formatDate = (val) => {
  if (!val) return "";
  const s = String(val).trim();
  if (!s) return "";
  // Already in DD/MM/YYYY-ish format — return as-is (don't reformat user-entered)
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(s)) return s;
  const d = new Date(s);
  if (isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
};

const last10 = (v) =>
  String(v ?? "")
    .replace(/\D/g, "")
    .slice(-10);

// Pick the dominant color from a region of pixel data using histogram
// quantization. Far better than mean averaging — a band that is mostly
// red but has a few decorative gold dots returns "red", not muddy brown.
//
// Strategy: bucket each pixel into a 32x32x32 RGB grid (4096 buckets total),
// count occurrences, return the bucket center with highest count.
const dominantColorFromRegion = (data, w, x0, y0, x1, y1) => {
  const buckets = new Map();
  let bestKey = -1, bestCount = 0;
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const i = (y * w + x) * 4;
      // Skip fully transparent pixels
      if (data[i + 3] < 128) continue;
      // 5-bit per channel (0..31) — 32^3 = 32768 buckets
      const r = data[i] >> 3;
      const g = data[i + 1] >> 3;
      const b = data[i + 2] >> 3;
      const key = (r << 10) | (g << 5) | b;
      const c = (buckets.get(key) || 0) + 1;
      buckets.set(key, c);
      if (c > bestCount) {
        bestCount = c;
        bestKey = key;
      }
    }
  }
  if (bestKey < 0) return null;
  // Decode — center of bucket (multiply by 8 then add 4)
  const r = ((bestKey >> 10) & 0x1f) * 8 + 4;
  const g = ((bestKey >> 5) & 0x1f) * 8 + 4;
  const b = (bestKey & 0x1f) * 8 + 4;
  return `rgb(${r},${g},${b})`;
};

// Smart image footer analyzer.
//
// Goal: every promotional image has a different empty "footer band" baked
// into its design — different position, different height, different color.
// We scan rows from the bottom upward, measure each row's color variance
// (low variance = flat/empty band, high variance = busy image content), and
// detect the largest contiguous block of low-variance rows at the bottom.
// That block is the band where firm name + mobile should be drawn.
//
// Returns:
//   { bandTopRatio, bandHeightRatio, color }
//   - bandTopRatio    : top of band as fraction of image height (0..1)
//   - bandHeightRatio : band height as fraction of image height (0..1)
//   - color           : "rgb(r,g,b)" — DOMINANT color of the band (histogram)
//
// Falls back to bottom 7% with dominant color if analysis fails.
const analyzeImageFooter = (img) => {
  const FALLBACK = {
    bandTopRatio: 0.93,
    bandHeightRatio: 0.07,
    color: "#0f172a",
  };
  try {
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return FALLBACK;

    // Downscale to a manageable working canvas — keep aspect ratio.
    const TARGET_W = 200;
    const scale = TARGET_W / w;
    const wW = TARGET_W;
    const wH = Math.max(40, Math.round(h * scale));

    const cv = document.createElement("canvas");
    cv.width = wW;
    cv.height = wH;
    const ctx = cv.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, wW, wH);
    const data = ctx.getImageData(0, 0, wW, wH).data;

    // Per-row mean RGB and per-row variance (sum of channel std deviations)
    const rowMean = new Array(wH);
    const rowVar = new Array(wH);
    for (let y = 0; y < wH; y++) {
      let sumR = 0, sumG = 0, sumB = 0;
      for (let x = 0; x < wW; x++) {
        const i = (y * wW + x) * 4;
        sumR += data[i];
        sumG += data[i + 1];
        sumB += data[i + 2];
      }
      const mr = sumR / wW;
      const mg = sumG / wW;
      const mb = sumB / wW;
      // Variance — sum of squared diffs from per-row mean across all 3 channels
      let v = 0;
      for (let x = 0; x < wW; x++) {
        const i = (y * wW + x) * 4;
        v += (data[i] - mr) ** 2 + (data[i + 1] - mg) ** 2 + (data[i + 2] - mb) ** 2;
      }
      rowMean[y] = [mr, mg, mb];
      rowVar[y] = v / wW; // average squared deviation per pixel
    }

    // Threshold: rows with variance below this are considered "flat".
    // Tuned empirically — 250 corresponds to ~stddev 16 across RGB which
    // catches solid colors, gentle gradients, and noisy-but-uniform fills,
    // while excluding text/icons/photographic content.
    const FLAT_THRESHOLD = 280;

    // Walk from the bottom up. Skip the last row(s) if they are an outlier
    // (e.g. a hard 1-2px border line). Then accumulate flat rows.
    let bandBottom = wH - 1;
    let bandTop = bandBottom;
    let consecutiveBusy = 0;
    const ALLOW_BUSY = 2; // tolerate up to 2 noisy rows inside the band
    for (let y = wH - 1; y >= 0; y--) {
      if (rowVar[y] < FLAT_THRESHOLD) {
        bandTop = y;
        consecutiveBusy = 0;
      } else {
        consecutiveBusy++;
        if (consecutiveBusy > ALLOW_BUSY) break;
      }
    }

    let bandH = bandBottom - bandTop + 1;

    // Clamp band height. Analyzer might detect a huge flat area (e.g.
    // entire yellow background of an Eid creative); we cap the band we
    // actually USE at 8% of image height so the firm-name strip stays
    // tight to the bottom edge and never dwarfs the artwork above it.
    const minH = Math.max(8, Math.round(wH * 0.04));
    const maxH = Math.round(wH * 0.08);
    if (bandH < minH) {
      // Couldn't find any flat region — use the fallback.
      const fbTop = Math.round(wH * 0.93);
      const fbColor =
        dominantColorFromRegion(data, wW, 0, fbTop, wW, wH) || "#0f172a";
      return { bandTopRatio: 0.93, bandHeightRatio: 0.07, color: fbColor };
    }
    // Cap to maxH but pin the band to the very bottom of the image —
    // sample color from the FULL detected flat area so we capture the
    // image's actual band tone, not just a sliver of it.
    const sampleTop = bandTop;
    const sampleBottom = bandBottom + 1;
    if (bandH > maxH) {
      bandH = maxH;
      bandTop = bandBottom + 1 - bandH;
    }

    const color =
      dominantColorFromRegion(data, wW, 0, sampleTop, wW, sampleBottom) ||
      "#0f172a";

    return {
      bandTopRatio: bandTop / wH,
      bandHeightRatio: bandH / wH,
      color,
    };
  } catch {
    return FALLBACK;
  }
};

const getContrastTextColor = (rgbStr) => {
  const m = rgbStr.match(/\d+/g);
  if (!m) return "#fff";
  const [r, g, b] = m.map(Number);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#0f172a" : "#ffffff";
};

/* ---------- Preview / Download composer ----------
 *
 * KEY GUARANTEE — the displayed image is ALWAYS the exact URL the user
 * clicked. We render the festive image with a plain <img src={imgSrc}>
 * (the SAME element the grid thumbnail uses) so there is zero chance of a
 * proxy / canvas / cache mismatch sneaking in a different picture.
 *
 * The firm name + mobile are drawn as a normal HTML/CSS overlay positioned
 * over the image's existing footer band. A separate background image is
 * loaded in parallel via the CORS-safe proxy to drive the smart band
 * color/position analyzer; if that load fails the overlay falls back to a
 * sensible fixed position with a dark band.
 *
 * Download uses html2canvas to snapshot the composed view — exactly what
 * the user sees on screen, branded with the chosen firm and number.
 */
function PreviewCanvas({
  imgSrc,
  imgName,
  firmName,
  mobile,
  customFooterText,
  onDownloadStart, // optional analytics hook — called when user clicks download
  onClose,
}) {
  const displayImgRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [bgColor, setBgColor] = useState("#0f172a");
  const [textColor, setTextColor] = useState("#ffffff");
  // AI-detected band geometry — different per image (position + height
  // depend on each template's design).
  const [bandRatio, setBandRatio] = useState({ top: 0.93, height: 0.07 });
  const [imgReady, setImgReady] = useState(false);
  // Natural dimensions of the DISPLAYED image (loaded via direct URL
  // with browser session — guaranteed to be the right image). Used to
  // detect when the analysis copy from the proxy returned a wrong image
  // (different aspect ratio = wrong file).
  const [displayDims, setDisplayDims] = useState(null);
  // The CORS-loaded copy of the image — used ONLY for the download canvas
  // and for footer-band pixel analysis. Display uses a separate plain
  // <img> with no crossOrigin so it renders even on hosts that don't
  // emit CORS headers (Google Drive thumbnails do this).
  const [analysisImg, setAnalysisImg] = useState(null);
  // Track WHICH source the analysis image actually came from — exposed in
  // the debug strip so we can tell at a glance whether lh3, proxy, or
  // the original URL succeeded for this card.
  const [analysisSource, setAnalysisSource] = useState("");

  // ── Send-to-WhatsApp (to a selected customer) ──
  const [showSend, setShowSend] = useState(false);
  const [custList, setCustList] = useState([]);
  const [custLoading, setCustLoading] = useState(false);
  const [custSearch, setCustSearch] = useState("");
  const [selected, setSelected] = useState({}); // mobile -> true
  const [sendSelf, setSendSelf] = useState(false); // also send to the dealer
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState("");
  // Bulk to customers is ON only when the dealer's Meta WhatsApp number is set up.
  const [metaBulk, setMetaBulk] = useState({ enabled: false, metaPhone: "" });

  const dealerPhone10 = (() => {
    const direct = String(localStorage.getItem("loginPhone") || "").replace(/\D/g, "");
    if (direct.length >= 10) return direct.slice(-10);
    try {
      const d = JSON.parse(localStorage.getItem("dealer") || "{}");
      const p = String(d.phone || d["Contact No"] || "").replace(/\D/g, "");
      if (p.length >= 10) return p.slice(-10);
    } catch {}
    return "";
  })();

  // Background loader — fetch a CORS-safe copy via the proxy so we can
  // (a) sample the footer band color and (b) draw it onto a canvas for
  // the download. The DISPLAYED image is independent of this — it's a
  // bare <img src={imgSrc}> in the JSX below — so even if proxy/CORS
  // fails, the user still sees the correct festive image.
  useEffect(() => {
    let cancelled = false;
    setBgColor("#0f172a");
    setTextColor("#ffffff");
    setBandRatio({ top: 0.93, height: 0.07 });
    setAnalysisImg(null);
    setAnalysisSource("");
    setDisplayDims(null);

    console.log("[PreviewCanvas] opened for", imgName, "→ imgSrc:", imgSrc);

    const applyAnalysis = (img) => {
      try {
        const info = analyzeImageFooter(img);
        if (cancelled) return true;
        setBgColor(info.color);
        setTextColor(getContrastTextColor(info.color));
        setBandRatio({ top: info.bandTopRatio, height: info.bandHeightRatio });
        return true;
      } catch {
        return false;
      }
    };

    const tryLoad = (src, withCors, next, label = "?") => {
      const img = new Image();
      if (withCors) img.crossOrigin = "anonymous";
      img.onload = () => {
        if (cancelled) return;
        console.log(
          `[PreviewCanvas] ✓ loaded via ${label} (${img.naturalWidth}×${img.naturalHeight}) → ${src}`
        );
        if (withCors) {
          // Stash for download canvas use, then run analysis. Both can
          // safely operate on the same CORS-clean image.
          setAnalysisImg(img);
          setAnalysisSource(label);
          if (applyAnalysis(img)) return;
          if (next) next();
        }
      };
      img.onerror = (e) => {
        if (cancelled) return;
        console.warn(`[PreviewCanvas] ✗ failed via ${label} → ${src}`);
        if (next) next();
      };
      img.src = src;
    };

    // Try sources in priority order. First CORS-clean load wins.
    //
    // CRITICAL — do NOT cache-bust the lh3 URL. Cache-busting forces a
    // fresh hit to Google's CDN every time which RATE-LIMITS us (429
    // Too Many Requests, observed in the field). lh3 is content-addressed
    // by file ID anyway so there's no stale-cache risk.
    //
    // We DO cache-bust the proxy URL because that goes through our own
    // backend where stale browser cache was a real problem.
    const driveId = driveIdFromUrl(imgSrc);
    const lh3Url = driveId
      ? `https://lh3.googleusercontent.com/d/${driveId}=w2000`
      : null;
    const proxied = `${SHEETS.imgProxy(imgSrc)}&_=${Date.now()}`;

    const stage4 = () => tryLoad(imgSrc, true, null, "imgSrc-direct"); // last resort, often CORS-blocked
    const stage3 = () => tryLoad(proxied, true, stage4, "proxy"); // proxy fallback
    const stage2 = () =>
      lh3Url ? tryLoad(lh3Url, true, stage3, "lh3-direct") : stage3();
    stage2();

    return () => {
      cancelled = true;
    };
  }, [imgSrc]);

  // Compute the firm/mobile label
  const cleanFirm = String(firmName || "").trim().toUpperCase();
  const cleanMob = String(mobile || "").trim();
  // If dealer typed something in the "Custom footer text" field, that
  // replaces ONLY the firm-name portion of the footer — mobile number
  // ALWAYS stays at the end. Empty custom text falls back to the firm
  // name. So the footer is always "<lead>-<mobile>" where <lead> is
  // either the custom text or the firm name.
  const customLine = String(customFooterText || "").trim();
  const lead = customLine || cleanFirm;
  const fullText =
    lead && cleanMob
      ? `${lead} - ${cleanMob}`
      : lead || cleanMob || "";


  // Helper — does the analysisImg actually match the displayed image,
  // or did the proxy/CDN return a different file (Drive's anonymous
  // server-fetch placeholder is a known case)? We compare aspect ratios
  // because the proxy may return the same logical image at a different
  // pixel size, but it should NEVER come back with a wildly different
  // shape — that's the placeholder swap we keep getting bitten by.
  const analysisMatchesDisplay = () => {
    if (!analysisImg || !displayDims) return false;
    const aDisp = displayDims.w / displayDims.h;
    const aAna = analysisImg.naturalWidth / analysisImg.naturalHeight;
    if (!isFinite(aDisp) || !isFinite(aAna)) return false;
    // Allow 2% tolerance for rounding / scaling differences.
    return Math.abs(aDisp - aAna) / aDisp < 0.02;
  };

  const safeFirm = (firmName || "creative")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "_");

  // Render firm/mobile overlay onto a same-origin blob image and
  // trigger a download. Same-origin (blob:) URLs don't taint the canvas
  // so toBlob() works and the saved file matches the on-screen preview.
  const bakeAndDownloadFromBlob = async (sourceBlob) => {
    const objUrl = URL.createObjectURL(sourceBlob);
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = (e) => reject(e);
        i.src = objUrl;
      });
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      if (fullText) {
        const bandTop = Math.round(h * bandRatio.top);
        const bandH = Math.round(h * bandRatio.height);
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, bandTop, w, bandH);

        const padX = Math.round(w * 0.015);
        const maxWidth = w - padX * 2;

        const meas = document.createElement("canvas").getContext("2d");
        const measure = (t, sz) => {
          meas.font = `bold ${sz}px sans-serif`;
          return meas.measureText(t).width;
        };

        // Try single line first — start at a generous font and shrink
        // until it fits. If we shrink past a readable minimum, switch
        // to a two-line layout (firm name on top, mobile underneath)
        // so the full name is ALWAYS visible — no truncation.
        const singleMax = Math.round(bandH * 0.5);
        const singleMin = Math.round(bandH * 0.34);
        let singleFs = singleMax;
        while (
          singleFs > singleMin &&
          measure(fullText, singleFs) > maxWidth
        ) {
          singleFs -= 1;
        }
        const singleFits =
          measure(fullText, singleFs) <= maxWidth && singleFs >= singleMin;

        ctx.fillStyle = textColor;
        ctx.textAlign = "center";

        if (singleFits) {
          ctx.font = `bold ${singleFs}px sans-serif`;
          ctx.textBaseline = "middle";
          ctx.fillText(fullText, w / 2, bandTop + bandH / 2);
        } else {
          // Two-line — firm on top, mobile below. Each line is
          // independently shrunk until it fits, so even very long
          // firm names appear in full.
          const top = lead || fullText;
          const bottom = cleanMob;
          const twoMax = Math.round(bandH * 0.44);
          const twoMin = Math.max(10, Math.round(bandH * 0.22));
          const fit = (txt) => {
            let s = twoMax;
            while (s > twoMin && measure(txt, s) > maxWidth) s -= 1;
            return s;
          };
          const fsTop = fit(top);
          const fsBot = bottom ? fit(bottom) : 0;
          const gap = Math.round(bandH * 0.05);
          const totalH = fsTop + (bottom ? fsBot + gap : 0);
          const startY = bandTop + (bandH - totalH) / 2;
          ctx.textBaseline = "top";
          ctx.font = `bold ${fsTop}px sans-serif`;
          ctx.fillText(top, w / 2, startY);
          if (bottom) {
            ctx.font = `bold ${fsBot}px sans-serif`;
            ctx.fillText(bottom, w / 2, startY + fsTop + gap);
          }
        }
      }

      const outBlob = await new Promise((res) =>
        canvas.toBlob(res, "image/png", 1)
      );
      if (!outBlob) throw new Error("toBlob returned null");
      const outUrl = URL.createObjectURL(outBlob);
      const a = document.createElement("a");
      a.href = outUrl;
      a.download = `${safeFirm}-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(outUrl), 1000);
      return true;
    } finally {
      URL.revokeObjectURL(objUrl);
    }
  };

  // Fallback download — fetch via the proxy (same-origin, full blob
  // access), bake the firm/mobile overlay onto it, and save as PNG so
  // the downloaded file matches what the user saw in the preview.
  const downloadOriginalDirect = async () => {
    try {
      const proxyUrl = `${SHEETS.imgProxy(imgSrc)}&_=${Date.now()}`;
      const resp = await fetch(proxyUrl);
      if (!resp.ok) {
        throw new Error(`proxy returned ${resp.status}`);
      }
      const blob = await resp.blob();
      if (!blob || blob.size === 0) {
        throw new Error("empty blob from proxy");
      }
      // Try to bake the overlay onto the proxied bytes — same-origin so
      // canvas isn't tainted and we can output a real PNG.
      try {
        const ok = await bakeAndDownloadFromBlob(blob);
        if (ok) return;
      } catch (e) {
        console.warn("[PreviewCanvas] bake-from-blob failed:", e);
      }
      // Last resort — save the unmodified proxied bytes (no overlay).
      const ext = (blob.type || "").includes("png") ? "png" : "jpg";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFirm}-${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.warn("[PreviewCanvas] proxy download failed:", err);
      // Final silent fallback — trigger an <a download> on the
      // original URL.
      const a = document.createElement("a");
      a.href = imgSrc;
      a.download = `${safeFirm}-${Date.now()}.jpg`;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  // Bake firm/mobile overlay onto a CORS-clean analysisImg and trigger
  // an automatic download. Returns true on success.
  // Only trusted when analysisSource is NOT 'proxy' (proxy is unreliable
  // for Drive — returns a placeholder image for anonymous server fetches).
  const tryBakedDownload = async () => {
    if (!analysisImg || !analysisMatchesDisplay()) return false;
    if (analysisSource === "proxy") {
      console.log(
        "[PreviewCanvas] skipping baked download — analysisImg is from proxy which is unreliable for Drive."
      );
      return false;
    }
    try {
      const imgW = analysisImg.naturalWidth;
      const imgH = analysisImg.naturalHeight;
      const canvas = document.createElement("canvas");
      canvas.width = imgW;
      canvas.height = imgH;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(analysisImg, 0, 0);

      if (fullText) {
        const bandTop = Math.round(imgH * bandRatio.top);
        const bandH = Math.round(imgH * bandRatio.height);
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, bandTop, imgW, bandH);

        const padX = Math.round(imgW * 0.015);
        const maxWidth = imgW - padX * 2;
        let fs = Math.round(bandH * 0.42);
        const minFs = Math.max(14, Math.round(bandH * 0.22));
        const meas = document.createElement("canvas").getContext("2d");
        const measure = (t, sz) => {
          meas.font = `bold ${sz}px sans-serif`;
          return meas.measureText(t).width;
        };
        while (fs > minFs && measure(fullText, fs) > maxWidth) fs -= 1;

        ctx.fillStyle = textColor;
        ctx.font = `bold ${fs}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(fullText, imgW / 2, bandTop + bandH / 2);
      }

      const blob = await new Promise((res) =>
        canvas.toBlob(res, "image/png", 1)
      );
      if (!blob) return false;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFirm}-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return true;
    } catch (e) {
      console.warn("[PreviewCanvas] baked download failed:", e);
      return false;
    }
  };

  // Download flow — RIGHT image is always more important than the
  // overlay, because Drive's anonymous server fetch (used by our proxy)
  // can return a placeholder/wrong image that matches dimensions of
  // real creatives, fooling our mismatch detection.
  //
  //   1. tryBakedDownload — only fires when analysisImg loaded from a
  //      CORS-clean BROWSER source (lh3-direct / imgSrc-direct). Those
  //      use the user's actual Drive session so the image is guaranteed
  //      correct. Bake the firm/mobile overlay onto it. Best-case path.
  //   2. Drive force-download URL — `?export=download` makes Drive send
  //      the file with `Content-Disposition: attachment` so the browser
  //      saves it automatically. Uses the user's session → guaranteed
  //      correct image. No overlay (we can't bake server-side without
  //      Drive auth), but right image > wrong image with overlay.
  const handleDownload = async () => {
    if (!imgReady) return;
    setDownloading(true);
    // Fire-and-forget analytics — don't block the download if it
    // fails. The parent decides what payload to send (so it can
    // include things like Festival/Promotional type and GSTIN).
    // Bake the footer onto the image ONCE — reuse it for both the download
    // AND the analytics log so the saved/downloaded file is the FILLED one.
    let baked = null;
    try {
      baked = await bakeToDataUrl();
    } catch {
      baked = null;
    }
    try {
      onDownloadStart?.({ imgSrc, imgName, baked });
    } catch {
      /* analytics never breaks downloads */
    }
    try {
      // 1. Footer-filled baked image (preferred).
      if (baked) {
        const a = document.createElement("a");
        a.href = baked;
        a.download = `${safeFirm}-${Date.now()}.jpg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }

      // 2. Drive force-download (raw, no overlay) — only if baking failed.
      const ddl = driveDownloadUrl(imgSrc);
      if (ddl) {
        const a = document.createElement("a");
        a.href = ddl;
        a.download = `${safeFirm}-${Date.now()}.jpg`;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }

      // 3. Non-Drive URL — last resort, save proxy bytes as-is.
      await downloadOriginalDirect();
    } catch (e) {
      alert("Download failed: " + (e?.message || e));
    } finally {
      setDownloading(false);
    }
  };

  // Bake the firm/mobile overlay onto the CORS-clean image and return a
  // JPEG dataURL (personalized). Returns null if the clean image isn't
  // available (then we fall back to the raw public URL for Maytapi).
  // Reliably produce a footer-FILLED JPEG dataURL. Loads a fresh CORS-clean
  // lh3 copy (lh3.googleusercontent.com sends CORS headers, so the canvas is
  // not tainted and toDataURL works) — independent of the flaky analysisImg
  // state, so the baked image always has the footer text.
  const bakeToDataUrl = async () => {
    try {
      let baseImg = analysisImg;
      const clean = baseImg && analysisMatchesDisplay() && analysisSource !== "proxy";
      if (!clean) {
        const driveId = driveIdFromUrl(imgSrc);
        const url = driveId
          ? `https://lh3.googleusercontent.com/d/${driveId}=w1400`
          : imgSrc;
        baseImg = await new Promise((res, rej) => {
          const i = new Image();
          i.crossOrigin = "anonymous";
          i.onload = () => res(i);
          i.onerror = rej;
          i.src = url;
        });
      }
      const imgW = baseImg.naturalWidth;
      const imgH = baseImg.naturalHeight;
      const canvas = document.createElement("canvas");
      canvas.width = imgW;
      canvas.height = imgH;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(baseImg, 0, 0);
      if (fullText) {
        const bandTop = Math.round(imgH * bandRatio.top);
        const bandH = Math.round(imgH * bandRatio.height);
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, bandTop, imgW, bandH);
        const padX = Math.round(imgW * 0.015);
        const maxWidth = imgW - padX * 2;
        let fs = Math.round(bandH * 0.42);
        const minFs = Math.max(14, Math.round(bandH * 0.22));
        const meas = document.createElement("canvas").getContext("2d");
        const measure = (t, sz) => {
          meas.font = `bold ${sz}px sans-serif`;
          return meas.measureText(t).width;
        };
        while (fs > minFs && measure(fullText, fs) > maxWidth) fs -= 1;
        ctx.fillStyle = textColor;
        ctx.font = `bold ${fs}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(fullText, imgW / 2, bandTop + bandH / 2);
      }
      return canvas.toDataURL("image/jpeg", 0.85);
    } catch (e) {
      return null;
    }
  };

  const openSendModal = async () => {
    setShowSend(true);
    setSendStatus("");
    setSelected({});
    setSendSelf(false);
    if (!dealerPhone10) return;
    getMetaBulkStatus().then((s) => setMetaBulk({ ...s, loaded: true }));
    setCustLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/customers?phone=${encodeURIComponent(dealerPhone10)}`,
        { headers: { "x-auth-phone": dealerPhone10 } }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success)
        setCustList(Array.isArray(data.customers) ? data.customers : []);
    } catch {
      /* keep empty */
    } finally {
      setCustLoading(false);
    }
  };

  const toggleCust = (mobile) =>
    setSelected((p) => ({ ...p, [mobile]: !p[mobile] }));

  const toggleAllCust = () => {
    const allOn =
      filteredCust.length > 0 && filteredCust.every((c) => selected[c.mobile]);
    const next = { ...selected };
    filteredCust.forEach((c) => {
      if (c.mobile) next[c.mobile] = !allOn;
    });
    setSelected(next);
  };

  // "Send to myself" → Maytapi (/api/send-creative). Selected customers → Meta
  // Cloud API from the dealer's OWN WhatsApp Business number — only when that
  // number is set up in the Meta phones sheet; otherwise bulk stays behind
  // the "Contact us" request.
  const handleSend = async () => {
    const self = sendSelf && !!dealerPhone10;
    const custPhones = metaBulk.enabled
      ? custList.filter((c) => c.mobile && selected[c.mobile]).map((c) => c.mobile)
      : [];
    if (!self && custPhones.length === 0) {
      setSendStatus(
        metaBulk.enabled
          ? "Select at least 1 customer or 'myself'."
          : "Only 'Send to myself' is available right now."
      );
      return;
    }
    setSending(true);
    setSendStatus("Preparing image…");
    const dataUrl = await bakeToDataUrl();
    const rawUrl = driveDownloadUrl(imgSrc) || imgSrc;
    const caption = ""; // send image only — no caption/footer text
    const filename = `${safeFirm}-creative.jpg`;

    const sendOne = async (mobile) => {
      const attempt = (body) =>
        fetch(`${API_BASE}/api/send-creative`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-auth-phone": dealerPhone10,
          },
          body: JSON.stringify({ phone: mobile, caption, filename, ...body }),
        });
      // Try personalized baked image first; fall back to raw URL if the
      // large body is blocked (WAF) or the send fails.
      if (dataUrl) {
        try {
          const r = await attempt({ image: dataUrl });
          if (r.ok) return true;
        } catch {}
      }
      try {
        const r = await attempt({ imageUrl: rawUrl });
        return r.ok;
      } catch {
        return false;
      }
    };

    const parts = [];
    if (self) {
      setSendStatus("Sending to you…");
      const ok = await sendOne(dealerPhone10);
      parts.push(ok ? "✓ Sent to your WhatsApp." : "❌ Send to you failed.");
    }
    if (custPhones.length) {
      let done = 0;
      let sent = 0;
      let firstError = "";
      setSendStatus(`Customers: 0/${custPhones.length}…`);
      await sendBulkViaMeta({
        phones: custPhones,
        image: dataUrl,
        imageUrl: rawUrl,
        filename,
        caption,
        onResult: (r) => {
          done++;
          if (r.ok) sent++;
          else if (!firstError) firstError = r.error || "";
          setSendStatus(`Customers: ${done}/${custPhones.length}…`);
        },
      });
      parts.push(
        sent === custPhones.length
          ? `✓ Sent to ${sent} customers.`
          : `Customers: ✓ ${sent} | ❌ ${custPhones.length - sent}${firstError ? ` (${firstError})` : ""}`
      );
    }
    setSending(false);
    setSendStatus(parts.join(" "));
  };

  const filteredCust = custList.filter((c) => {
    const q = custSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      String(c.name || "").toLowerCase().includes(q) ||
      String(c.mobile || "").includes(q)
    );
  });
  const custSelectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b gap-3">
          <h3 className="font-bold text-gray-900 truncate">
            Preview & Download
            {imgName ? (
              <span className="ml-2 text-gray-500 font-medium text-sm">
                · {imgName}
              </span>
            ) : null}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 text-2xl leading-none shrink-0"
          >
            ×
          </button>
        </div>
        <div className="p-4 flex flex-col items-center gap-4">
          {/* Display view — bare <img> with NO crossOrigin so Google Drive
              thumbnails (which don't send CORS headers) render normally,
              just like the grid thumbnails do. The image URL is the exact
              same one the clicked card uses, so what you see here always
              matches the card. */}
          <div
            className="relative inline-block max-w-full rounded-lg overflow-hidden shadow-md border bg-gray-100"
            style={{ maxHeight: "70vh" }}
          >
            <img
              ref={displayImgRef}
              key={imgSrc}
              src={imgSrc}
              alt={imgName || "Festive creative"}
              referrerPolicy="no-referrer"
              onLoad={(e) => {
                setImgReady(true);
                const el = e.currentTarget;
                if (el?.naturalWidth && el?.naturalHeight) {
                  setDisplayDims({
                    w: el.naturalWidth,
                    h: el.naturalHeight,
                  });
                  console.log(
                    `[PreviewCanvas] display image natural size: ${el.naturalWidth}×${el.naturalHeight}`
                  );
                }
              }}
              onError={() => setImgReady(true)}
              className="block max-w-full h-auto"
              style={{ maxHeight: "70vh" }}
            />
            {imgReady && fullText && (
              <div
                className="absolute left-0 right-0 flex items-center justify-center text-center px-2"
                style={{
                  top: `${bandRatio.top * 100}%`,
                  height: `${bandRatio.height * 100}%`,
                  background: bgColor,
                  color: textColor,
                  fontWeight: 800,
                  // Use container-query units when available so the
                  // font shrinks proportionally with the band. Long
                  // firm names now wrap onto two lines instead of being
                  // truncated with an ellipsis.
                  fontSize: "clamp(9px, 2.8vw, 20px)",
                  lineHeight: 1.05,
                  letterSpacing: "0.01em",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                <span className="block w-full whitespace-normal">
                  {fullText}
                </span>
              </div>
            )}
            {!imgReady && (
              <div className="py-20 px-10 text-gray-500 text-center">
                Loading image...
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={handleDownload}
              disabled={!imgReady || downloading}
              className={`px-6 py-3 rounded-xl font-bold shadow-md transition ${
                imgReady && !downloading
                  ? "bg-yellow-500 hover:bg-yellow-600 text-black"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {downloading ? "Preparing..." : "⬇ Download"}
            </button>
            <button
              onClick={openSendModal}
              disabled={!imgReady}
              className={`px-6 py-3 rounded-xl font-bold shadow-md transition flex items-center gap-2 ${
                imgReady
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.736-.98z" />
              </svg>
              Send to WhatsApp
            </button>
          </div>
        </div>
      </div>

      {/* ── Customer-select + send modal ── */}
      {showSend && (
        <div
          className="fixed inset-0 z-[210] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => !sending && setShowSend(false)}
        >
          <div
            className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[88vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="text-lg font-extrabold text-gray-800">
                Send to Customers
              </h3>
              <button
                onClick={() => !sending && setShowSend(false)}
                className="text-gray-500 hover:text-gray-900 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="px-5 pt-3 space-y-2">
              {/* Send to myself */}
              {dealerPhone10 && (
                <label
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer border transition ${
                    sendSelf
                      ? "border-green-400 bg-green-50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={sendSelf}
                    onChange={() => setSendSelf((s) => !s)}
                    className="w-4 h-4 accent-green-600"
                  />
                  <div className="w-9 h-9 rounded-full bg-green-600 text-white flex items-center justify-center shrink-0">
                    📱
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-gray-800 text-sm">Send to myself</p>
                    <p className="text-xs text-gray-500">+91 {dealerPhone10}</p>
                  </div>
                </label>
              )}
              {/* Not connected yet → how to connect their own WhatsApp Business number */}
              {metaBulk.loaded && !metaBulk.enabled && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                  <p className="text-sm font-bold text-blue-900">
                    📲 Send to your customers from your own WhatsApp
                  </p>
                  <p className="text-xs text-blue-800 mt-0.5">
                    Connect your WhatsApp Business number once (about 10 minutes). After that you can share creatives with all your customers from here.
                  </p>
                  <a
                    href="/whatsapp-connect-steps.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 flex w-full justify-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded-lg transition text-sm"
                  >
                    View connection steps (PDF)
                  </a>
                </div>
              )}

              {/* Customers — only when the dealer's WhatsApp Business number is active */}
              {metaBulk.enabled && (
                <>
                  <div className="rounded-xl border border-green-300 bg-green-50 p-3">
                    <p className="text-sm font-bold text-green-900">
                      📦 Bulk sharing ON
                    </p>
                    <p className="text-xs text-green-800 mt-0.5">
                      Selected customers will receive it from your WhatsApp Business number
                      {metaBulk.metaPhone ? ` +91 ${metaBulk.metaPhone}` : ""}.
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs font-bold text-gray-600">
                      Customers ({filteredCust.length})
                    </span>
                    <button
                      type="button"
                      onClick={toggleAllCust}
                      className="text-xs font-bold text-gray-500 hover:text-gray-800"
                    >
                      Select All
                    </button>
                  </div>
                  <input
                    value={custSearch}
                    onChange={(e) => setCustSearch(e.target.value)}
                    placeholder="Search by name or mobile…"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3">
              {!dealerPhone10 ? (
                <p className="text-center text-gray-500 py-8 text-sm">
                  Login number not detected. Please log in again.
                </p>
              ) : !metaBulk.enabled ? null : custLoading ? (
                <p className="text-center text-gray-400 py-8 text-sm">Loading…</p>
              ) : filteredCust.length === 0 ? (
                <p className="text-center text-gray-500 py-8 text-sm">
                  {custList.length === 0
                    ? "No customers yet. Add them in 'My Customers' first."
                    : "No matches found."}
                </p>
              ) : (
                <div className="space-y-1.5">
                  {filteredCust.map((c) => (
                    <label
                      key={c._id || c.mobile}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer border transition ${
                        selected[c.mobile]
                          ? "border-green-400 bg-green-50"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={!!selected[c.mobile]}
                        onChange={() => toggleCust(c.mobile)}
                        className="w-4 h-4 accent-green-600"
                      />
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 text-white flex items-center justify-center font-bold shrink-0">
                        {String(c.name || "?")[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-gray-800 truncate text-sm">
                          {c.name}
                        </p>
                        <p className="text-xs text-gray-500">+91 {c.mobile}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t bg-white rounded-b-3xl">
              {sendStatus && (
                <p className="text-xs font-semibold text-gray-600 mb-2 text-center">
                  {sendStatus}
                </p>
              )}
              <button
                onClick={handleSend}
                disabled={sending || (!sendSelf && !(metaBulk.enabled && custSelectedCount > 0))}
                className="w-full bg-green-600 text-white font-bold py-3 rounded-xl hover:bg-green-700 disabled:opacity-50 transition"
              >
                {sending
                  ? "Sending…"
                  : metaBulk.enabled
                    ? `Send to WhatsApp (${(sendSelf ? 1 : 0) + custSelectedCount})`
                    : "Send to WhatsApp (myself)"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Main page ---------- */
const PromotionalPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // previewItem tracks BOTH the image src and the title — using just a URL
  // as the key wasn't enough because two rows can share the same URL, in
  // which case React kept the old PreviewCanvas mounted with stale state.
  // We also bump a counter on every open so even re-clicking the SAME image
  // forces a fresh mount + re-analyze.
  const [previewItem, setPreviewItem] = useState(null);
  const previewCounterRef = useRef(0);
  const openPreview = useCallback((item) => {
    if (!item?.link) return;
    previewCounterRef.current += 1;
    setPreviewItem({
      src: item.link,
      name: item.name,
      counter: previewCounterRef.current,
    });
  }, []);
  const closePreview = useCallback(() => setPreviewItem(null), []);

  // All filter state lives in the shared CreativesFiltersContext so the
  // Header (rendered as a sibling, not a parent) can also read/write the
  // same filters. The header renders the actual filter inputs in its own
  // yellow row when on /personal-creatives or /promotional routes.
  const ctx = useCreativesFilters() || {};
  const {
    dealer,
    dealerMobile,
    dateFilter = "",
    setDateFilter = () => {},
    nameFilter = "",
    setNameFilter = () => {},
    setShowCalendar = () => {},
    selectedFirm = "",
    mobileInput = "",
    customFooterText = "",
    setCustomFooterText = () => {},
    setAvailableDates = () => {},
  } = ctx;

  const navigate = useNavigate();
  const location = useLocation();

  // Route-aware default type:
  //   /personal-creatives → "Festival"
  //   /promotional        → "Promotional"
  const isFestivalRoute = location.pathname.startsWith("/personal-creatives");
  const defaultType = isFestivalRoute ? "Festival" : "Promotional";
  const typeFilter = defaultType;

  // Reset date / name / custom-footer filters when the route flips so the
  // new view starts clean (e.g. a date selected on Festival page shouldn't
  // carry over to Promotional page).
  useEffect(() => {
    setDateFilter("");
    setNameFilter("");
    setCustomFooterText("");
    setShowCalendar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultType]);

  // Show cache instantly so refresh me loader spinner na flash ho
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      if (Array.isArray(cached) && cached.length) {
        setRows(cached);
        setLoading(false);
      }
    } catch {}
  }, []);

  // Fetch promotional images — used both on mount and polling
  const fetchPromotional = useCallback(async () => {
    try {
      const res = await fetch(API_URL, { cache: "no-store" });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const text = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error("Invalid JSON from API");
      }
      const list = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.data)
          ? parsed.data
          : [];
      const filtered = list.filter((r) => {
        const ap = String(r?.Approval || r?.approval || "").trim().toLowerCase();
        if (!ap) return true;
        return ["approved", "yes", "y", "ok", "true", "1"].includes(ap);
      });
      setRows(filtered);
      setError("");
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(filtered));
      } catch {}
    } catch (e) {
      setError(e?.message || "Failed to load promotional images");
    } finally {
      setLoading(false);
    }
  }, []);

  // First fetch on mount — backend ko bhi force refresh kar do taaki sheet ka
  // latest data turant Mongo me aaye, fir frontend wahi le.
  useEffect(() => {
    // Fire-and-forget — don't block the first paint on Apps Script
    // latency. The cached payload renders instantly; fresh data lands
    // when the background refresh completes (next poll picks it up).
    forceRefreshSheet("promotional").catch(() => {});
    fetchPromotional();
  }, [fetchPromotional]);

  // Auto-poll every 60s for live updates from sheet
  usePolling(fetchPromotional, 60_000);

  const items = useMemo(() => {
    return rows.map((r, i) => {
      const link =
        r?.["Festive Image Link"] ||
        r?.["Festive Image"] ||
        r?.["Image"] ||
        r?.["image"] ||
        r?.["URL"] ||
        r?.["url"] ||
        "";
      const rawDate = r?.Date || r?.date || "";
      return {
        id: `${i}`,
        type: detectType(r),
        rawDate,
        dateKey: toInputDate(rawDate), // YYYY-MM-DD for filter compare
        date: formatDate(rawDate),     // DD/MM/YYYY for display
        name: r?.Name || r?.name || "Untitled",
        remarks: r?.Remarks || r?.remarks || "",
        link: toDriveDirect(link),
      };
    });
  }, [rows]);

  // Today's key in YYYY-MM-DD form — used both to decide the badge type
  // for each card AND to filter out past creatives by default. Memoized
  // at render-time on a date-only basis so the cutoff stays correct after
  // midnight without manual refresh.
  const todayKey = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  }, []);

  // Filtered list — applies type, date, and name (substring) filters.
  //
  // Default behaviour DIFFERS between the two routes:
  //   FESTIVAL  → only TODAY + UPCOMING + undated show by default. Past
  //               festivals stay accessible via the calendar OR name
  //               search (search drops the date restriction so users can
  //               find e.g. last year's Diwali creative).
  //   PROMOTIONAL → ALL items show by default, no past-hiding. A
  //               promotional offer rarely "expires" the way a festival
  //               date does, so dealers want to see everything.
  const filteredItems = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    const isSearching = q.length > 0;
    const hidesPastByDefault = typeFilter === "Festival";
    const filtered = items.filter((it) => {
      if (typeFilter && typeFilter !== "All" && it.type !== typeFilter)
        return false;
      if (dateFilter) {
        // Explicit date selected — show only that exact date.
        if (it.dateKey !== dateFilter) return false;
      } else if (hidesPastByDefault && !isSearching) {
        // Festival default — hide past dates. Undated items still show.
        // Search relaxes this so older festivals can be found by name.
        if (it.dateKey && it.dateKey < todayKey) return false;
      }
      if (q && !String(it.name).toLowerCase().includes(q)) return false;
      return true;
    });

    // Sort by date category so the grid reads top-to-bottom:
    //   1. Today           — most relevant, appears first
    //   2. Upcoming        — ascending by date (closest first)
    //   3. Previous (past) — descending by date (most recent first)
    //   4. Undated         — at the very end
    const groupOf = (it) => {
      if (!it.dateKey) return 3;
      if (it.dateKey === todayKey) return 0;
      return it.dateKey > todayKey ? 1 : 2;
    };
    return filtered.slice().sort((a, b) => {
      const ga = groupOf(a);
      const gb = groupOf(b);
      if (ga !== gb) return ga - gb;
      // Same group → date-order within group
      if (ga === 1) return a.dateKey.localeCompare(b.dateKey); // upcoming asc
      if (ga === 2) return b.dateKey.localeCompare(a.dateKey); // past desc
      return 0; // today + undated keep input order
    });
  }, [items, typeFilter, dateFilter, nameFilter, todayKey]);

  // Available dates for the active type filter — published to the
  // shared context so the header's date picker can highlight days that
  // actually have creatives.
  const availableDates = useMemo(() => {
    const set = new Set();
    items.forEach((it) => {
      if (typeFilter && typeFilter !== "All" && it.type !== typeFilter) return;
      if (it.dateKey) set.add(it.dateKey);
    });
    return [...set].sort();
  }, [items, typeFilter]);

  // Push availableDates into shared context whenever they change so the
  // date picker (rendered inside the Header) can highlight them. Reset
  // to [] on unmount so other pages don't get stale highlighted dates.
  useEffect(() => {
    setAvailableDates(availableDates);
    return () => setAvailableDates([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDates]);

  // Categorize a YYYY-MM-DD key as "today", "upcoming", "past", or "none".
  const getBadgeType = (key) => {
    if (!key) return "none";
    if (key === todayKey) return "today";
    return key > todayKey ? "upcoming" : "past";
  };

  // Route-derived UI labels — same component, two visual identities.
  const pageTitle = typeFilter === "Festival" ? "Festival Creatives" : "Promotional Creatives";
  const emptyMsg =
    typeFilter === "Festival"
      ? "No festival creatives match the current filters."
      : "No promotional creatives match the current filters.";

  if (!dealer) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-700 mb-4">Please log in to view promotional creatives.</p>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 bg-yellow-500 rounded-lg font-semibold"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    // Mobile header has the filter bar wrapping onto multiple rows so it
    // needs more vertical breathing room than desktop.
    <div className="min-h-screen bg-gray-50 pt-[12rem] sm:pt-[10rem] md:pt-[6rem] pb-12 px-4">
      {/* Calendar styling — yellow highlight for dates that have creatives,
          dark fill for the currently-selected date, mobile-friendly width. */}
      <style>{`
        .sota-cal-pop .react-datepicker {
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          box-shadow: 0 12px 28px rgba(0,0,0,0.15);
          font-family: inherit;
          overflow: hidden;
        }
        .sota-cal-pop .react-datepicker__triangle { display: none; }
        .sota-cal-pop .react-datepicker__header {
          background: #fff;
          border-bottom: 1px solid #f1f5f9;
          padding-top: 10px;
        }
        .sota-cal-pop .react-datepicker__current-month {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 6px;
        }
        .sota-cal-pop .react-datepicker__day-name,
        .sota-cal-pop .react-datepicker__day {
          width: 2rem;
          line-height: 2rem;
          margin: 2px;
          border-radius: 8px;
          font-size: 13px;
        }
        .sota-cal-pop .react-datepicker__day-name { color: #64748b; font-weight: 700; }
        .sota-cal-pop .sota-cal-day { color: #0f172a; }
        .sota-cal-pop .sota-cal-day:hover { background: #f1f5f9; }
        .sota-cal-pop .sota-cal-day-available {
          background: #fef3c7 !important;
          color: #92400e !important;
          font-weight: 800 !important;
          border: 1px solid #facc15 !important;
        }
        .sota-cal-pop .sota-cal-day-selected,
        .sota-cal-pop .react-datepicker__day--selected {
          background: #0f172a !important;
          color: #ffffff !important;
          font-weight: 800 !important;
          border: 1px solid #0f172a !important;
        }
        .sota-cal-pop .react-datepicker__day--keyboard-selected {
          background: transparent;
          color: inherit;
        }
        @media (max-width: 640px) {
          .sota-cal-pop {
            position: fixed !important;
            left: 50% !important;
            top: 50% !important;
            transform: translate(-50%, -50%);
            z-index: 100;
            width: 92vw;
            max-width: 360px;
          }
          .sota-cal-pop .react-datepicker { width: 100%; }
          .sota-cal-pop .react-datepicker__month-container { width: 100%; }
          .sota-cal-pop .react-datepicker__day-name,
          .sota-cal-pop .react-datepicker__day {
            width: calc((100% - 28px) / 7);
          }
        }
      `}</style>
      <div className="max-w-7xl mx-auto">
        {/* The filter row (Date / Name / Firm / Mobile) lives in the
            Header now — see Header.jsx CreativesFiltersBar. State is
            shared via CreativesFiltersContext. */}

        {/* States */}
        {loading && rows.length === 0 ? (
          <div className="text-center py-20 text-gray-500">Loading creatives...</div>
        ) : error ? (
          <div className="text-center py-20 text-red-600">{error}</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-20 text-gray-500">{emptyMsg}</div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-8 gap-2 md:gap-3">
            {filteredItems.map((it) => {
              const badge = getBadgeType(it.dateKey);
              const badgeStyles = {
                today:
                  "bg-yellow-400 text-slate-900 border border-yellow-500",
                upcoming:
                  "bg-blue-500 text-white border border-blue-600",
                past:
                  "bg-slate-700 text-white border border-slate-800",
              };
              const badgeLabel = {
                today: "Today",
                upcoming: "Upcoming",
                past: "Previous",
              };
              return (
              <div
                key={it.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition overflow-hidden flex flex-col"
              >
                <button
                  type="button"
                  onClick={() => openPreview(it)}
                  disabled={!it.link}
                  className={`relative bg-gray-50 aspect-square w-full flex items-center justify-center overflow-hidden ${
                    it.link ? "cursor-pointer hover:opacity-90" : "cursor-default"
                  }`}
                  title={it.link ? "Click to preview & download" : "No image"}
                >
                  {it.link ? (
                    <img
                      src={it.link}
                      alt={it.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="text-gray-400 text-[10px]">No image</span>
                  )}
                  {/* Top-right badge — Today / Upcoming / Previous */}
                  {badge !== "none" && (
                    <span
                      className={`absolute top-1 right-1 px-1.5 py-0.5 rounded-md text-[8px] sm:text-[9px] font-extrabold uppercase tracking-wide shadow-sm ${badgeStyles[badge]}`}
                    >
                      {badgeLabel[badge]}
                    </span>
                  )}
                </button>
                <div className="px-1.5 py-1.5 flex-1 flex flex-col">
                  <p className="font-semibold text-gray-900 text-[10px] line-clamp-1 mb-0.5 leading-tight">
                    {it.name}
                  </p>
                  {it.date && (
                    <p className="text-[9px] text-gray-500 mb-1">{it.date}</p>
                  )}
                  <button
                    onClick={() => openPreview(it)}
                    disabled={!it.link}
                    className={`mt-auto w-full py-1 rounded font-bold text-[10px] shadow-sm ${
                      it.link
                        ? "bg-yellow-500 hover:bg-yellow-600 text-black"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Preview
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {previewItem && (
        // key uses a per-click counter — guarantees a fresh mount every
        // single time the user opens a preview, even if they re-click the
        // same image or two rows share a URL. Stale `imgEl` / canvas
        // pixels can never carry over.
        <PreviewCanvas
          key={previewItem.counter}
          imgSrc={previewItem.src}
          imgName={previewItem.name}
          firmName={selectedFirm}
          mobile={mobileInput || dealerMobile}
          customFooterText={customFooterText}
          onDownloadStart={async ({ imgSrc, imgName, baked }) => {
            // Look up the dealer's GSTIN for the currently-selected firm.
            // Multiple fallbacks because the previous logic missed cases
            // where the cache key was off (mobile format mismatch) or the
            // firm-name comparison was strict (case/space differences):
            //   1. firmsCache (Login pre-warm) — fast, in localStorage
            //   2. Try several key/name normalizations
            //   3. Fall back to fetching dealers sheet (server-filtered
            //      by phone, small response — runs in ~200ms)
            //   4. Last resort: scan ALL firmsCache:v1:* keys
            const phone10 = String(dealerMobile || "")
              .replace(/\D/g, "")
              .slice(-10);
            const norm = (s) =>
              String(s || "")
                .toLowerCase()
                .replace(/\s+/g, " ")
                .trim();
            const wantedFirm = norm(selectedFirm);
            const pickGstFromFirm = (f) =>
              String(
                f?.gst ||
                  f?.GST ||
                  f?.gstin ||
                  f?.GSTIN ||
                  f?.["GSTIN/UIN"] ||
                  f?.["GST No"] ||
                  ""
              ).trim();

            let gstin = "";

            // 1. firmsCache under normalized 10-digit key
            try {
              const raw = localStorage.getItem(`firmsCache:v1:${phone10}`);
              if (raw) {
                const parsed = JSON.parse(raw);
                const firms = parsed?.payload?.firms || [];
                const match = firms.find(
                  (f) => norm(f?.firmName) === wantedFirm
                );
                if (match) gstin = pickGstFromFirm(match);
                // If no exact match by firm, but cache only has one firm
                // and it has a GST, use that.
                if (!gstin && firms.length === 1) {
                  gstin = pickGstFromFirm(firms[0]);
                }
              }
            } catch {
              /* fall through */
            }

            // 2. Scan ALL firmsCache:v1:* keys (handles mobile-key mismatch)
            if (!gstin) {
              try {
                for (let i = 0; i < localStorage.length; i++) {
                  const key = localStorage.key(i) || "";
                  if (!key.startsWith("firmsCache:v1:")) continue;
                  const raw = localStorage.getItem(key);
                  if (!raw) continue;
                  const parsed = JSON.parse(raw);
                  const firms = parsed?.payload?.firms || [];
                  const match = firms.find(
                    (f) => norm(f?.firmName) === wantedFirm
                  );
                  if (match) {
                    gstin = pickGstFromFirm(match);
                    if (gstin) break;
                  }
                }
              } catch {
                /* fall through */
              }
            }

            // 3. Fall back to dealer object (registration data may have GST)
            if (!gstin && dealer) {
              const dealerFirmName = norm(
                dealer?.firmName ||
                  dealer?.Particulars ||
                  dealer?.["Firm Name"]
              );
              if (dealerFirmName === wantedFirm) {
                gstin = String(
                  dealer?.gst ||
                    dealer?.GST ||
                    dealer?.gstin ||
                    dealer?.GSTIN ||
                    dealer?.["GSTIN/UIN"] ||
                    dealer?.["GST No"] ||
                    ""
                ).trim();
              }
            }

            // 4. Network fallback — server-filtered dealers fetch
            if (!gstin && phone10) {
              try {
                const res = await fetch(SHEETS.dealers(phone10), {
                  cache: "no-store",
                });
                const data = await res.json().catch(() => []);
                const rows = Array.isArray(data) ? data : [];
                const match =
                  rows.find(
                    (r) =>
                      norm(
                        r?.Particulars ||
                          r?.["Firm Name"] ||
                          r?.["Firm Name "] ||
                          r?.["Company Name"]
                      ) === wantedFirm
                  ) ||
                  // If no firm match, but only one row, use it
                  (rows.length === 1 ? rows[0] : null);
                if (match) {
                  gstin = String(
                    match?.["GSTIN/UIN"] ||
                      match?.["GSTIN/UIN "] ||
                      match?.["GST No"] ||
                      match?.["GST No "] ||
                      match?.GST ||
                      match?.gst ||
                      ""
                  ).trim();
                }
              } catch {
                /* swallow — empty gstin is still loggable */
              }
            }

            gstin = String(gstin || "").toUpperCase();

            // Indian-style readable timestamp for the sheet — DD/MM/YYYY
            // HH:MM:SS in IST, e.g. "12/06/2026 16:32:11". Much easier
            // for a human to scan than ISO format. Also include the
            // raw ISO string in case anyone wants to sort programmatically.
            const now = new Date();
            const pad = (n) => String(n).padStart(2, "0");
            // Convert to IST regardless of the browser's local timezone
            const ist = new Date(now.getTime() + (5.5 * 60 - now.getTimezoneOffset()) * 60_000);
            const tsReadable =
              `${pad(ist.getUTCDate())}/${pad(ist.getUTCMonth() + 1)}/` +
              `${ist.getUTCFullYear()} ` +
              `${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}:${pad(ist.getUTCSeconds())}`;

            // Actual footer baked on the image (same logic as PreviewCanvas):
            // <custom-text or FIRM> - <mobile>. Logged so the sheet shows what
            // the dealer's footer actually said, even when not customised.
            const fMob = String(mobileInput || phone10 || dealerMobile || "").trim();
            const fLead =
              String(customFooterText || "").trim() ||
              String(selectedFirm || "").trim().toUpperCase();
            const footerText =
              fLead && fMob ? `${fLead} - ${fMob}` : fLead || fMob || "";

            logCreativeDownload({
              timestamp: tsReadable,               // "12/06/2026 16:32:11"
              timestampISO: now.toISOString(),     // raw, for sorting
              type: typeFilter, // "Festival" or "Promotional"
              creativeName: imgName || "",
              imageUrl: imgSrc || "",
              dealerMobile: mobileInput || phone10 || dealerMobile || "",
              firmName: selectedFirm || "",
              gstin,
              // The full footer text printed on the image.
              customFooterText: footerText,
              footerText,
              // Footer-FILLED image (base64). The Apps Script saves this to
              // Drive and writes that link in the sheet's Image URL column.
              imageBase64: baked || "",
            });
          }}
          onClose={closePreview}
        />
      )}
    </div>
  );
};

export default PromotionalPage;
